import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';
import { AuthContext } from './AuthContext'; // 👈 Required
import {
  fetchAllPlans,
  addPlan,
  deletePlan,
  fetchPlanById,
  addRecipeToPlan,
  removeRecipeFromPlan
} from '../services/plannerService'; 

const PlannerContext = createContext();

export function PlannerProvider({ children }) {
  const [plans, setPlans] = useState([]);
  const [planMeals, setPlanMeals] = useState([]); // ✅ Used for meal entries
  const [activePlan, setActivePlan] = useState(null); // ✅ NEW
  const [loading, setLoading] = useState(false);
  const { auth, loading: authLoading } = useContext(AuthContext);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const plans = await fetchAllPlans();
      // console.log("📦 plans from API", plans);
      setPlans(plans);

      if (plans.length > 0) {
        const latestPlan = plans[plans.length - 1];
        setActivePlan(latestPlan);

        // 🔄 Fetch recipe entries for that plan
        const fullPlan = await fetchPlanById(latestPlan.id);
        setPlanMeals(fullPlan.recipes || []);
        // console.log("📌 Fetched planMeals:", fullPlan.recipes);
      }
    } catch (err) {
      console.error('❌ Failed to fetch plans', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && auth.isAuthenticated) {
      fetchAll();
    }
  }, [authLoading, auth.isAuthenticated, fetchAll]);

  const createPlan = async (planData) => {
    try {
      const newPlan = await addPlan(planData); // Save to backend
      await fetchAll();                        // 🔄 Refetch full list to update dropdown
      setActivePlan(newPlan);                  // 🎯 Make the new plan active
      return newPlan;
    } catch (err) {
      console.error('Failed to create plan', err);
      throw err;
    }
  };

  const addToPlan = async (planId, recipeEntry) => {
    try {
      await addRecipeToPlan(planId, recipeEntry);             // 🔁 API call to backend
      const updated = await fetchPlanById(planId);            // 🔄 Get updated plan
      setPlanMeals(updated.recipes || []);                    // ✅ Only update planMeals
    } catch (err) {
      console.error('❌ Failed to add recipe to plan:', err);
    }
  };

  const removeFromPlan = async (entryId, planId) => {
    try {
      await removeRecipeFromPlan(planId, entryId);            // 🔁 Remove via backend
      const updated = await fetchPlanById(planId);            // 🔄 Refetch updated plan
      setPlanMeals(updated.recipes || []);                    // ✅ Refresh just planMeals
    } catch (err) {
      console.error('❌ Failed to remove recipe from plan:', err);
    }
  };

  const deleteMealPlan = async (planId) => {
    try {
      await deletePlan(planId);
      await fetchAll(); // Refresh plans
    } catch (err) {
      console.error('❌ Failed to delete plan', err);
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('planner');
  const openPlanner = () => { setActiveTab('planner'); setIsOpen(true); };
  const openClientBooks = () => { setActiveTab('clientBooks'); setIsOpen(true); };
  const closeDrawer = () => setIsOpen(false);

  return (
    <PlannerContext.Provider value={{
      plans,
      activePlan,
      setActivePlan,
      planMeals,
      loading,
      fetchAll,
      addToPlan,
      removeFromPlan,
      createPlan,
      deleteMealPlan,
      bottomDrawerOpen: isOpen,
      activeTab,
      openPlanner,
      openClientBooks,
      closeDrawer,
    }}>
      {children}
    </PlannerContext.Provider>
  );
}

export function usePlanner() {
  const ctx = useContext(PlannerContext);
  if (!ctx) throw new Error('usePlanner must be used within a PlannerProvider');
  return ctx;
}
